import {
  Button,
  Input,
  PopConfirm,
  useBreadcrumbRoutes,
  Wrapper,
  Tooltip,
  Tree,
  Empty,
  FormContent,
} from '@maxtropy/components';
import CreateArea, { ComponentMode } from './components/CreateorViewArea';
import React, { useEffect, useMemo, useState } from 'react';
import { Affix, Col, Row, Space } from 'antd';
import styles from './index.module.scss';
import {
  FileSearchOutlined,
  FormOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';

import { apiV2AreaDeletePost, apiV2AreaTreePost } from '@maxtropy/device-customer-apis-v2';
import { DataNode } from 'antd/es/tree';

import { OrganizationTreeProps, TreeDataProps, getAllKeys, findPathToNode } from './utils';
import AreaClassificationSetting from './components/AreaClassificationSetting';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import VideoMonitorConfigModal from './components/VideoMonitorConfigModal';

export interface parentInfo {
  parentId?: number;
  zoneName: string;
}

const AreaAdministration = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [showCreateArea, setShowCreateArea] = useState<boolean>(false);
  const [mode, setMode] = useState<ComponentMode>('create');
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [currentNodeId, setCurrentNodeId] = useState<number>();
  const [parentCodeInfo, setParentCodeInfo] = useState<parentInfo>();
  const [treeData, setTreeData] = useState<TreeDataProps[]>([]);
  const [searchParam, setSearchParam] = useState<string>('');
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [expandedKeys, setExpandKeys] = useState<any[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);
  const [showClassificationSetting, setShowClassificationSetting] = useState<boolean>(false);
  const [showVideoMonitorConfig, setShowVideoMonitorConfig] = useState<boolean>(false);
  const [currentMonitorAreaId, setCurrentMonitorAreaId] = useState<number | string>();
  const [currentMonitorAreaName, setCurrentMonitorAreaName] = useState<string>('');
  const hasB_CATEGORYSETTINGS = useHasPermission(PermissionsType.B_CATEGORYSETTINGS);
  const getAreaTree = () => {
    apiV2AreaTreePost().then(data => {
      const dataList = data.list || [];
      const treeData: TreeDataProps[] = dataList?.map(item => ({
        ...item,
        rootNode: true,
      }));
      setTreeData(treeData);
    });
  };
  useEffect(() => {
    getAreaTree();
  }, []);

  const formatTreeData = (data: TreeDataProps[]) => {
    return data?.map(item => {
      const searchText = `${item.level}#${item.zoneName}`;
      const res: any = {
        key: String(item.id) ?? '',
        searchText,
        title: titleFormat(item, item.rootNode),
        children: formatTreeData(item.children || []),
      };
      return res;
    });
  };

  const focusOnNodeInTree = (targetKey: React.Key) => {
    const fullTree = formatTreeData(treeData);
    const path = findPathToNode(fullTree, targetKey);
    const parentKeys = path.slice(0, -1);
    setExpandKeys(parentKeys);
    setSelectedKeys([targetKey]);
    setAutoExpandParent(true);
  };

  const onSelect = (selectedKeys: React.Key[], e: any) => {
    if (e.selected) {
      setSelectedKeys(selectedKeys);
    }
  };

  const titleFormat = (data: OrganizationTreeProps, rootNode?: boolean) => {
    return (
      <div className={styles.treeNodeContainer}>
        <span className={styles.nodeLabel}>{data.zoneName}</span>
        <Space className={styles.titleFormatIcon}>
          {!isEdit ? (
            <>
              <Tooltip title="查看">
                <FileSearchOutlined
                  onClick={() => {
                    setCurrentNodeId(data.id);
                    setMode('view');
                    setShowCreateArea(true);
                  }}
                />
              </Tooltip>
            </>
          ) : (
            <>
              {/*编辑*/}
              <Tooltip title="编辑">
                <FormOutlined
                  onClick={() => {
                    setMode('edit');
                    setShowCreateArea(true);
                    setCurrentNodeId(data.id);
                  }}
                />
              </Tooltip>
              {/*新增*/}

              <Tooltip title="新增">
                <PlusCircleOutlined
                  onClick={() => {
                    setMode('create');
                    setShowCreateArea(true);
                    setParentCodeInfo({
                      parentId: data?.id,
                      zoneName: data?.zoneName || '',
                    });
                  }}
                />
              </Tooltip>
              {/*删除*/}
              <Tooltip title="删除">
                <PopConfirm
                  title="删除后不可恢复，请确认是否删除？"
                  onConfirm={() => {
                    apiV2AreaDeletePost({ id: data?.id }).then(() => getAreaTree());
                  }}
                >
                  <MinusCircleOutlined />
                </PopConfirm>
              </Tooltip>
              {/*视频监控*/}
              <Tooltip title="视频监控">
                <VideoCameraOutlined
                  onClick={() => {
                    setCurrentMonitorAreaId(data?.id);
                    setCurrentMonitorAreaName(data?.zoneName || '');
                    setShowVideoMonitorConfig(true);
                  }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      </div>
    );
  };

  const list = useMemo(() => {
    if (!searchParam.trim()) {
      return formatTreeData(treeData);
    }
    const keyword = searchParam.trim();
    const fullTree = formatTreeData(treeData);
    const flatNodes: DataNode[] = [];

    const flattenAndFilter = (nodes: DataNode[]) => {
      nodes.forEach(node => {
        const text = (node as any).searchText ?? '';
        if (text.includes(keyword)) {
          flatNodes.push({
            ...node,
            children: [],
          });
        }
        if (node.children) {
          flattenAndFilter(node.children as DataNode[]);
        }
      });
    };

    flattenAndFilter(fullTree);
    return flatNodes;
  }, [treeData, searchParam, isEdit]);

  const fullFormattedTree = useMemo(() => {
    return formatTreeData(treeData);
  }, [treeData]);

  const allExpandedKeys = useMemo(() => {
    return getAllKeys(fullFormattedTree);
  }, [fullFormattedTree]);

  useEffect(() => {
    if (fullFormattedTree.length > 0) {
      setExpandKeys(allExpandedKeys);
      setAutoExpandParent(false);
    }
  }, [fullFormattedTree, allExpandedKeys]);

  const onExpand = (expandedKeys: any[]) => {
    setExpandKeys(expandedKeys);
    setAutoExpandParent(false);
  };

  const handleExpandAll = () => {
    setExpandKeys(allExpandedKeys);
    setAutoExpandParent(false);
  };

  const handleCollapseAll = () => {
    setExpandKeys([]);
    setAutoExpandParent(false);
  };
  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]}>
      <FormContent narrow title={'区域管理'}>
        <Row style={{ width: '100%' }} gutter={[192, 0]} className={styles.treeContent}>
          <Col span={12}>
            <Input.Search
              allowClear
              placeholder="请输入区域名称"
              value={searchParam}
              onChange={e => setSearchParam(e.target.value)}
              onSearch={v => setSearchParam(v)}
              onClear={() => {
                setSearchParam('');
                if (selectedKeys !== null) {
                  focusOnNodeInTree(selectedKeys[0]);
                }
              }}
            />
            <Space size={8} style={{ margin: '16px 0' }}>
              <Button
                icon={<PlusOutlined />}
                onClick={() => {
                  setMode('create');
                  setShowCreateArea(true);
                }}
              >
                新建区域
              </Button>
              <Button onClick={() => handleExpandAll()}>全部展开</Button>
              <Button onClick={() => handleCollapseAll()}>全部收起</Button>
              {/* {hasB_CATEGORYSETTINGS && */}
              <Button onClick={() => setShowClassificationSetting(true)}>分类设置</Button>
              {/* } */}
            </Space>
            {list && list.length > 0 ? (
              <Tree
                height={500}
                showLine={{ showLeafIcon: false }}
                treeData={list}
                expandedKeys={expandedKeys}
                selectedKeys={selectedKeys}
                onExpand={onExpand}
                autoExpandParent={autoExpandParent}
                onSelect={onSelect}
              />
            ) : (
              <Empty style={{ marginTop: '10%' }} />
            )}
            <Space className="sticky-footer">
              {!isEdit && list && list.length > 0 && (
                <Button type="primary" onClick={() => setIsEdit(true)}>
                  编辑
                </Button>
              )}
              {isEdit && <Button onClick={() => setIsEdit(false)}>退出编辑状态</Button>}
            </Space>
          </Col>
        </Row>
        {showCreateArea && (
          <CreateArea
            mode={mode}
            currentNodeId={currentNodeId}
            createInfo={parentCodeInfo}
            closeModal={() => setShowCreateArea(false)}
            update={getAreaTree}
          />
        )}
        <AreaClassificationSetting
          visible={showClassificationSetting}
          onCancel={() => setShowClassificationSetting(false)}
        />
        {showVideoMonitorConfig && (
          <VideoMonitorConfigModal
            visible={showVideoMonitorConfig}
            areaId={currentMonitorAreaId}
            areaName={currentMonitorAreaName}
            onCancel={() => setShowVideoMonitorConfig(false)}
          />
        )}
      </FormContent>
    </Wrapper>
  );
};
export default AreaAdministration;
